import { convertToImperialDate } from "app/codes";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAssets } from "service/assets";
import { getWellSummary, getWellSummaryDict } from "service/wellSummary";
import useLoading from "components/common/useLoading";
import {
  Spin,
  Table,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  Select,
  Card,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { titleCase } from "utils/dataUtil";

function AutoHiddenScrollBar(props) {
  const [showScrollBar, setShowScrollBar] = useState("hidden");
  return (
    <div
      style={{ overflowX: showScrollBar }}
      onMouseOver={() => setShowScrollBar("auto")}
      onMouseLeave={() => setShowScrollBar("hidden")}
    >
      {props.children}
    </div>
  );
}

// ----------------------- helpers ---------------------------------------------

// Smart comparator: treat numeric strings as numbers; null/empty sink to bottom.
function smartSorter(field) {
  return (a, b) => {
    const va = a[field];
    const vb = b[field];
    const aNull = va === null || va === undefined || va === "";
    const bNull = vb === null || vb === undefined || vb === "";
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    const na = Number(va);
    const nb = Number(vb);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && va !== "" && vb !== "") {
      return na - nb;
    }
    return String(va).localeCompare(String(vb));
  };
}

// Detect column type from a sample of records. Numeric / date / string only.
function detectColumnType(records, field) {
  for (let i = 0; i < records.length && i < 50; i++) {
    const v = records[i][field];
    if (v == null || v === "") continue;
    if (/^\d{4}-\d{2}-\d{2}/.test(String(v))) return "date";
    if (!Number.isNaN(Number(v))) return "number";
    return "string";
  }
  return "string";
}

const OPS = {
  string: [
    { value: "contains", label: "contains" },
    { value: "equals", label: "=" },
    { value: "not_equals", label: "≠" },
    { value: "starts_with", label: "starts with" },
    { value: "ends_with", label: "ends with" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "not empty" },
  ],
  number: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "≠" },
    { value: "gt", label: ">" },
    { value: "gte", label: "≥" },
    { value: "lt", label: "<" },
    { value: "lte", label: "≤" },
    { value: "between", label: "between" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "not empty" },
  ],
  date: [
    { value: "equals", label: "=" },
    { value: "gt", label: "after" },
    { value: "lt", label: "before" },
    { value: "between", label: "between" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "not empty" },
  ],
};

function evalFilter(record, f) {
  const v = record[f.column];
  if (f.op === "is_empty") return v == null || v === "";
  if (f.op === "is_not_empty") return !(v == null || v === "");
  if (v == null || v === "") return false;
  const sv = String(v).toLowerCase();
  const fv = String(f.value ?? "").toLowerCase();
  switch (f.op) {
    case "contains":
      return sv.includes(fv);
    case "equals":
      return sv === fv;
    case "not_equals":
      return sv !== fv;
    case "starts_with":
      return sv.startsWith(fv);
    case "ends_with":
      return sv.endsWith(fv);
    case "gt":
      return Number(v) > Number(f.value);
    case "gte":
      return Number(v) >= Number(f.value);
    case "lt":
      return Number(v) < Number(f.value);
    case "lte":
      return Number(v) <= Number(f.value);
    case "between": {
      const n = Number(v);
      const lo = Number(f.value);
      const hi = Number(f.value2);
      if (Number.isNaN(n) || Number.isNaN(lo) || Number.isNaN(hi)) return false;
      return n >= lo && n <= hi;
    }
    default:
      return true;
  }
}

function isFilterReady(f) {
  if (f.op === "is_empty" || f.op === "is_not_empty") return !!f.column;
  if (!f.column || !f.op) return false;
  if (f.op === "between") {
    return f.value !== "" && f.value !== undefined && f.value2 !== "" && f.value2 !== undefined;
  }
  return f.value !== "" && f.value !== undefined && f.value !== null;
}

// ----------------------- main component -------------------------------------

function WellSummaryEditor() {
  const [LoadingIcon, showLoadingIcon, hideLoadingIcon] = useLoading(
    <Spin size="large" />,
    50
  );
  const [wellSummaryHeaders, setWellSummaryHeaders] = useState([]);
  const [assets, setAssets] = useState(null);
  const [activeAssetId, setActiveAssetId] = useState(null);
  const [activeAssetName, setActiveAssetName] = useState(null);
  const [wells, setWells] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // Draft rows in the panel (user is editing) vs. activeFilters (currently applied).
  const [draftRows, setDraftRows] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    showLoadingIcon();
    getAssets(
      (result) => {
        setAssets(result);
        hideLoadingIcon();
      },
      () => hideLoadingIcon()
    );
  }, [showLoadingIcon, hideLoadingIcon]);

  useEffect(() => {
    showLoadingIcon();
    getWellSummaryDict(
      (result) => {
        setWellSummaryHeaders(
          result.map((item) => ({
            name: item.element_fullname || titleCase(item.element),
            value: item.element,
          }))
        );
        hideLoadingIcon();
      },
      () => hideLoadingIcon()
    );
  }, [showLoadingIcon, hideLoadingIcon]);

  useEffect(() => {
    if (assets == null || assets.length === 0) return;
    setActiveAssetId(assets[0].id);
    setActiveAssetName(assets[0].asset_name);
  }, [assets]);

  useEffect(() => {
    if (activeAssetId == null) return;
    showLoadingIcon();
    getWellSummary(
      activeAssetId,
      (result) => {
        setWells(result || []);
        // Reset filters when switching asset (column values differ).
        setActiveFilters([]);
        setDraftRows([]);
        hideLoadingIcon();
      },
      () => hideLoadingIcon()
    );
  }, [activeAssetId, hideLoadingIcon, showLoadingIcon]);

  function handleAssetChanged(value) {
    const asset = assets.find((a) => a.asset_name === value);
    if (asset) {
      setActiveAssetName(asset.asset_name);
      setActiveAssetId(asset.id);
    }
  }

  // Column types per field, derived from the loaded wells (sample first 50).
  const colTypes = useMemo(() => {
    const m = {};
    for (const h of wellSummaryHeaders) {
      m[h.value] = detectColumnType(wells, h.value);
    }
    return m;
  }, [wells, wellSummaryHeaders]);

  // -------- Antd Table columns: width sized to header label + sort icon -----
  // No per-column filter UI here — filtering is handled by the unified panel
  // above the table. Only sort + auto width.
  const columns = useMemo(() => {
    const sizeForHeader = (label) =>
      Math.max(110, Math.min(360, label.length * 9 + 50));
    const seqCol = {
      title: "#",
      key: "_seq",
      width: 60,
      fixed: "left",
      render: (_, __, idx) => idx + 1,
    };
    const dynamic = wellSummaryHeaders.map((h) => ({
      title: h.name,
      dataIndex: h.value,
      key: h.value,
      width: sizeForHeader(h.name),
      sorter: smartSorter(h.value),
      sortDirections: ["ascend", "descend", "ascend"],
      render: (val) => {
        if (val == null || val === "") return "—";
        if (h.value === "spud_date") return convertToImperialDate(val);
        return val;
      },
    }));
    return [seqCol, ...dynamic];
  }, [wellSummaryHeaders]);

  const totalWidth = useMemo(
    () => columns.reduce((s, c) => s + (c.width || 140), 0),
    [columns]
  );

  // -------- Filter pipeline: global search + activeFilters -----------------
  const filteredWells = useMemo(() => {
    let rows = wells;
    if (globalSearch) {
      const q = globalSearch.toLowerCase();
      rows = rows.filter((w) => {
        const hay = `${w.well_name || ""} ${w.uwi || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    if (activeFilters.length) {
      rows = rows.filter((r) => activeFilters.every((f) => evalFilter(r, f)));
    }
    return rows;
  }, [wells, globalSearch, activeFilters]);

  // -------- Filter panel actions --------------------------------------------
  const colLabel = useCallback(
    (v) => wellSummaryHeaders.find((h) => h.value === v)?.name || v,
    [wellSummaryHeaders]
  );

  const opLabel = useCallback((opVal) => {
    for (const list of Object.values(OPS)) {
      const found = list.find((o) => o.value === opVal);
      if (found) return found.label;
    }
    return opVal;
  }, []);

  const columnSelectOptions = useMemo(
    () =>
      wellSummaryHeaders.map((h) => ({
        value: h.value,
        label: h.name,
      })),
    [wellSummaryHeaders]
  );

  const opOptionsFor = useCallback(
    (column) => OPS[colTypes[column] || "string"],
    [colTypes]
  );

  function addDraftRow() {
    setDraftRows((r) => [
      ...r,
      { column: undefined, op: "contains", value: "", value2: "" },
    ]);
  }

  function updateDraft(idx, patch) {
    setDraftRows((rows) =>
      rows.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, ...patch };
        // When changing column, reset op to a sensible default of new type.
        if ("column" in patch) {
          const t = colTypes[patch.column] || "string";
          const ops = OPS[t];
          next.op = ops[0].value;
          next.value = "";
          next.value2 = "";
        }
        // When changing op away from `between`, clear value2; toward `between`, keep blank.
        if ("op" in patch && patch.op !== "between") next.value2 = "";
        return next;
      })
    );
  }

  function removeDraftRow(idx) {
    setDraftRows((r) => r.filter((_, i) => i !== idx));
  }

  function applyDrafts() {
    const ready = draftRows.filter(isFilterReady);
    setActiveFilters(ready);
  }

  function clearAll() {
    setDraftRows([]);
    setActiveFilters([]);
  }

  function removeActiveFilter(idx) {
    setActiveFilters((rows) => rows.filter((_, i) => i !== idx));
    // also pull it out of the draft if matching
    setDraftRows((rows) => rows.filter((_, i) => i !== idx));
  }

  // -------- Render -----------------------------------------------------------
  return (
    <>
      <LoadingIcon />

      <div
        style={{
          padding: "12px 24px 0",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* Top toolbar: asset selector + global search + filter toggle ------ */}
        <Space size="middle" wrap>
          <Select
            value={activeAssetName || undefined}
            onChange={handleAssetChanged}
            placeholder="Select asset"
            style={{ minWidth: 180 }}
            showSearch
            options={(assets || []).map((a) => ({
              value: a.asset_name,
              label: a.asset_name,
            }))}
          />
          <Input
            placeholder="Search well name or UWI…"
            prefix={<SearchOutlined />}
            allowClear
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            style={{ width: 320 }}
          />
          <Button
            icon={<FilterOutlined />}
            type={filterPanelOpen ? "primary" : "default"}
            onClick={() => setFilterPanelOpen((v) => !v)}
          >
            {filterPanelOpen ? "Hide filters" : "Filters"}
            {activeFilters.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                {activeFilters.length}
              </Tag>
            )}
          </Button>
          <Tag color="geekblue">{filteredWells.length} wells</Tag>
        </Space>

        {/* Filter panel (collapsible) -------------------------------------- */}
        {filterPanelOpen && (
          <Card
            size="small"
            title={
              <span>
                <FilterOutlined /> Filters
                <span style={{ color: "#888", fontWeight: "normal", marginLeft: 8 }}>
                  Combine multiple conditions (AND).
                </span>
              </span>
            }
            extra={
              <Button
                size="small"
                icon={<CloseOutlined />}
                type="text"
                onClick={() => setFilterPanelOpen(false)}
              />
            }
          >
            {draftRows.length === 0 && (
              <div style={{ color: "#888", marginBottom: 12 }}>
                No filters yet. Click <strong>Add filter</strong> to start.
              </div>
            )}
            {draftRows.map((row, idx) => {
              const t = colTypes[row.column] || "string";
              const numericOp = ["gt", "gte", "lt", "lte", "between"].includes(row.op);
              const isUnary = row.op === "is_empty" || row.op === "is_not_empty";
              return (
                <Space
                  key={idx}
                  align="start"
                  style={{ display: "flex", marginBottom: 10, flexWrap: "wrap" }}
                >
                  <Select
                    value={row.column}
                    onChange={(v) => updateDraft(idx, { column: v })}
                    placeholder="Column"
                    showSearch
                    optionFilterProp="label"
                    style={{ width: 220 }}
                    options={columnSelectOptions}
                  />
                  <Select
                    value={row.op}
                    onChange={(v) => updateDraft(idx, { op: v })}
                    style={{ width: 140 }}
                    disabled={!row.column}
                    options={opOptionsFor(row.column)}
                  />
                  {!isUnary &&
                    (numericOp ? (
                      <InputNumber
                        value={row.value === "" ? null : row.value}
                        onChange={(v) => updateDraft(idx, { value: v ?? "" })}
                        placeholder={t === "date" ? "YYYY-MM-DD" : "value"}
                        style={{ width: 150 }}
                      />
                    ) : (
                      <Input
                        value={row.value}
                        onChange={(e) => updateDraft(idx, { value: e.target.value })}
                        placeholder="value"
                        style={{ width: 200 }}
                        allowClear
                      />
                    ))}
                  {row.op === "between" && (
                    <>
                      <span style={{ color: "#888" }}>and</span>
                      <InputNumber
                        value={row.value2 === "" ? null : row.value2}
                        onChange={(v) => updateDraft(idx, { value2: v ?? "" })}
                        placeholder="upper bound"
                        style={{ width: 150 }}
                      />
                    </>
                  )}
                  <Button
                    icon={<DeleteOutlined />}
                    type="text"
                    danger
                    onClick={() => removeDraftRow(idx)}
                  />
                </Space>
              );
            })}
            <Space>
              <Button icon={<PlusOutlined />} onClick={addDraftRow}>
                Add filter
              </Button>
              <Button type="primary" onClick={applyDrafts}>
                Apply
              </Button>
              <Button onClick={clearAll}>Clear all</Button>
            </Space>
          </Card>
        )}

        {/* Active-filter chips above the table (closable) ------------------- */}
        {activeFilters.length > 0 && (
          <Space size={[4, 8]} wrap>
            <span style={{ color: "#888" }}>Active filters:</span>
            {activeFilters.map((f, i) => (
              <Tag
                key={i}
                color="blue"
                closable
                onClose={(e) => {
                  e.preventDefault();
                  removeActiveFilter(i);
                }}
              >
                {colLabel(f.column)} {opLabel(f.op)}
                {f.op !== "is_empty" && f.op !== "is_not_empty" && (
                  <>
                    {" "}
                    {f.value}
                    {f.op === "between" ? ` – ${f.value2}` : ""}
                  </>
                )}
              </Tag>
            ))}
          </Space>
        )}

        {/* Table (sticky header + horizontal scroll) ------------------------ */}
        <Table
          columns={columns}
          dataSource={filteredWells}
          rowKey={(record) => record.uwi || record.id}
          size="small"
          sticky
          scroll={{ x: totalWidth, y: "calc(100vh - 280px)" }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100", "200"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} wells`,
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/well_editor/${record.uwi}`, { replace: false }),
            style: { cursor: "pointer" },
          })}
        />
      </div>
    </>
  );
}

export default WellSummaryEditor;
export { AutoHiddenScrollBar };
